<template>
  <button class="btn btn-sm btn-secondary" @click="show"><font-awesome-icon icon="upload"/> {{$t('uploadFailedContacts')}}
    <b-modal v-model="showForm" :title="$t('uploadFailedContacts')" @close="close" no-close-on-esc no-close-on-backdrop no-enforce-focus>
      <div class="form-group">
        <label><b>{{ $t('downloadTemplate') }}</b></label>
        <div>
          <button @click="downloadTemplate" class="btn btn-sm btn-secondary mr-1 mb-2">
            <i class="fa fa-fw fa-lg fa-file-excel-o"></i>upload-failed-contacts-template.xlsx
          </button>
        </div>
      </div>
      <div class="form-group">
        <label><b>{{ $t('selectFile') }}</b></label>
        <b-form-file class="mb-3"
                     v-model="file"
                     :placeholder="$t('fileInputPlaceholder')"
                     :drop-placeholder="$t('fileInputPlaceholder')"
                     :browse-text="$t('selectFile')"
                     :disabled="reading||totalRows>0"
                     accept="application/x-iwork-keynote-sffnumbers,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ></b-form-file>
      </div>
      <div v-if="reading" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>
        <span>{{ $t('importReadingNotice') }}</span>
      </div>
      <div v-if="importing" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>
        <span>{{ $t('importingNotice') }}</span>
      </div>
      <div class="mb-3" v-if="totalRows">
        <b-progress :max="totalRows">
          <b-progress-bar :value="successCount+errorCount" :label="`${(((successCount + errorCount) / totalRows) * 100).toFixed(0)}%`"></b-progress-bar>
        </b-progress>
        <div class="text-center">{{ successCount + errorCount }}/{{ totalRows }} | {{ $t('errors') }}: {{ errorCount }}
          <a @click="downloadErrors" class="btn-link text-primary" href="javascript:;">[{{ $t('detail') }}]</a>
        </div>
      </div>
      <template v-slot:modal-footer>
        <button class="btn btn-secondary" @click="cancel" v-if="!reading&&importing">{{ $t('cancel') }}</button>
        <button class="btn btn-primary" @click="reset" v-if="!reading&&!importing&&totalRows>0">Reset</button>
        <button v-if="totalRows===0" class="btn btn-primary" @click="process" :disabled="reading||importing">
          <b-spinner small v-if="reading||importing"></b-spinner>
          {{ $t('import') }}
        </button>
      </template>
    </b-modal>
  </button>
</template>

<script>
import fileReaderStream from 'filereader-stream';
import {saveAs} from 'file-saver'
import Excel from 'exceljs';

export default {
  props: ['campaign'],
  data: function () {
    return {
      showForm: false,
      file: null,
      reading: false,
      importing: false,
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
      importErrors: [],
      cancelRequest: false,
    }
  },
  methods: {
    show() {
      this.showForm = true;
    },
    close(evt) {
      if (this.reading || this.importing) {
        evt.preventDefault();
      } else {
        this.reset();
      }
    },
    reset() {
      this.totalRows = 0;
      this.successCount = 0;
      this.errorCount = 0;
      this.importErrors = [];
      this.cancelRequest = false;
      this.reading = false;
      this.importing = false;
      this.file = null;
    },
    process(evt) {
      evt.preventDefault();
      if (!this.file) {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('importFileRequired'),
          icon: 'error',
        });
        return;
      }
      if (this.file.name.endsWith('.xls') || this.file.name.endsWith('.xlsx')) {
        this.reading = true;
        let readStream = fileReaderStream(this.file);
        const workbook = new Excel.Workbook();
        workbook.xlsx.read(readStream).then(async () => {
          if (workbook.worksheets.length === 0) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let sheet = workbook.worksheets[0];
          if (sheet.actualRowCount < 2) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          if (!sheet.getRow(1).values.find(v => v && v.trim() === 'phone')) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let headerRow = sheet.getRow(1);
          let i = 2;
          this.totalRows = sheet.actualRowCount - 1;
          this.reading = false;
          this.importing = true;
          while (i <= sheet.actualRowCount) {
            if (this.cancelRequest) {
              break;
            }
            let item = this.getItem(headerRow, sheet.getRow(i));
            try {
              await this.$bgService.post('/message-campaign/add-failed-contact/' + this.campaign, item);
              this.successCount++;
            } catch (err) {
              this.importErrors.push({row: i, err: err});
              this.errorCount++;
            }
            i++;
          }
        }).catch(err => {
          this.$swal({
            title: this.$t('error'),
            text: err.message,
            icon: 'error',
          });
        }).finally(() => {
          this.reading = false;
          this.importing = false;
          this.cancelRequest = false;
          this.$bus.$emit('loadSMSCampaign');
        });
      } else {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('invalidFileType'),
          icon: 'error',
        });
      }
    },
    cancel() {
      this.cancelRequest = true;
    },
    downloadErrors() {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Import errors');
      sheet.columns = [
        {header: this.$t('rowNumber'), key: 'row', width: 15},
        {header: this.$t('errors'), key: 'error', width: 50},
      ];
      for (let err of this.importErrors) {
        let errMsg = err.err.message;
        if (err.err.response) {
          if (err.err.response.status === 422) {
            errMsg = err.err.response.data.map(i => `${i.param}: ${i.msg}`).join(', ')
          } else if (err.err.response.data) {
            errMsg = err.err.response.data.message;
          }
        }
        sheet.addRow({row: err.row, error: errMsg});
      }
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'sms-campaign-failed-contact-import-error.xlsx'
        )
      });
    },
    downloadTemplate: function () {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('SMS Ads Campaign failed contact');
      sheet.columns = [
        {header: 'phone', key: 'phone', width: 15},
        {header: 'sendErrorMsg', key: 'sendErrorMsg', width: 15}
      ];
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'sms-campaign-failed-contact-import.xlsx'
        )
      });
    },
    getItem(headerRow, row) {
      let item = {};
      for (let j = 1; j <= headerRow.cellCount; j++) {
        let key = headerRow.getCell(j).value;
        let value;
        if (row.getCell(j).isHyperlink) {
          value = row.getCell(j).value.text;
        } else {
          value = row.getCell(j).value;
        }
        if (value) {
          if (typeof value === 'string') {
            value = value.trim();
          }
          item[key] = value;
        }
      }
      return item;
    }
  },
  i18n: {
    messages: {
      en: {
        uploadFailedContacts: "Upload failed contacts",
        downloadTemplate: "Download template",
        selectFile: "Select file",
        fileInputPlaceholder: "Choose file from your computer",
        importReadingNotice: "Reading data...",
        importingNotice: "Uploading data...",
        errors: "Errors",
        cancel: "Cancel",
        import: "Import",
        importFileRequired: "File is required",
        invalidFileTemplate: "Invalid file template",
        invalidFileType: "Invalid file type",
        rowNumber: "Row number",
        detail: "Detail"
      },
      vi: {
        uploadFailedContacts: "Tải lên DS LH lỗi",
        downloadTemplate: "Download file mẫu",
        selectFile: "Chọn file",
        fileInputPlaceholder: "Chọn file từ máy tính của bạn",
        importReadingNotice: "Đang đọc dữ liệu...",
        importingNotice: "Đang tải dữ liệu lên...",
        errors: "Lỗi",
        cancel: "Huỷ",
        import: "Tải lên",
        importFileRequired: "Cần chọn file",
        invalidFileTemplate: "File không đúng định dạng",
        invalidFileType: "File không đúng kiểu",
        rowNumber: "Số hàng",
        detail: "Chi tiết"
      }
    }
  }
}
</script>